/**
 * API-Schicht für `AppointmentsPage.js` (06/2026 Refactor Phase 1).
 *
 * Reine Datenabruf-Funktionen — kein State, keine Side-Effects. Jeder Caller
 * (React-Komponente) ist selbst dafür verantwortlich, die zurückgegebenen
 * Daten in seinen State zu schreiben (`setX(data)`).
 *
 * Vorteil: Endpoints zentral pflegbar, einheitliches Error-Handling, der
 * Page-File wird ~250 LOC kleiner.
 */
import axios from 'axios';
import { toLocalYMD } from '@/components/appointments/calendarUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/**
 * FLEX-Termine sind in MongoDB pro Kunde getrennt. Für den Kalender werden
 * Dokumente mit derselben flex_slot_id rein visuell nebeneinander gruppiert.
 * Jede Teilnehmer-Kachel behält dabei ihr eigenes Appointment-Dokument.
 */
export const groupFlexAppointmentsForCalendar = (appointments = []) => {
  const groups = new Map();

  // 06/2026 Fix (Kristina-Alf-Doll-Fall LIVE): Zwei FLEX-Teilnehmer, die
  // WIRKLICH denselben Slot teilen (gleicher Service + Startzeit + Trainer),
  // aber KEINE gemeinsame flex_slot_id besitzen (z.B. eine verschobene
  // Serien-Instanz mit flex_slot_id=null trifft auf eine Einzelbuchung ganz
  // ohne flex_slot_id), wurden bisher als zwei separate, exakt überlappende
  // Blöcke an derselben Position gerendert → einer verdeckte den anderen und
  // fehlte im Kalender (in der Mobile-App, die flach listet, war er sichtbar).
  // Deshalb: Termine ohne flex_slot_id, die sich Service+Startzeit+Trainer
  // teilen, per synthetischem Schlüssel zusammenführen. Voraussetzung sind
  // ≥2 solche Termine — Einzeltermine behalten ihr bisheriges Verhalten.
  // 09/2026 Fix (gemischte Zeitzonen-Formate): Termine desselben Slots können
  // als '...+02:00' ODER '...+00:00' (UTC, alte Chatbot-Buchungen) gespeichert
  // sein — derselbe Zeitpunkt, aber unterschiedlicher String. Für die
  // Gruppierung IMMER auf den Zeit-Instant (Epoch) normalisieren, sonst landen
  // instant-gleiche Teilnehmer in getrennten Gruppen (Phantom-Freiplatz).
  const instantOf = (s) => {
    if (!s) return '';
    const t = new Date(s).getTime();
    return Number.isNaN(t) ? String(s) : String(t);
  };
  // Slot-Schlüssel = Service + Start-Instant + Trainer. Alle Teilnehmer, die
  // WIRKLICH denselben Slot teilen, bekommen denselben Schlüssel — egal ob mit
  // oder ohne flex_slot_id.
  const slotKeyOf = (a) =>
    `${a.service_id || a.service_name || ''}|${instantOf(a.start_time)}|${a.trainer_id || ''}`;
  // Zähle ALLE (nicht-Reservations-)Termine pro Slot — INKL. solcher MIT
  // flex_slot_id (früher wurden diese ausgeschlossen).
  const slotKeyCount = new Map();
  appointments.forEach((a) => {
    if (a._isReservation) return;
    if (!a.trainer_id || !a.start_time) return;
    if (!a.service_id && !a.service_name) return;
    const k = slotKeyOf(a);
    slotKeyCount.set(k, (slotKeyCount.get(k) || 0) + 1);
  });

  appointments.forEach((appointment) => {
    // 10/2026 Fix (Daniela-Hohenberger-Fall LIVE): Ein FLEX-Slot kann
    // Teilnehmer MIT gemeinsamer flex_slot_id UND Teilnehmer OHNE flex_slot_id
    // mischen (z.B. Andrea+Daniela mit flex_slot_id, Nicole ohne). Früher
    // ergaben sich daraus zwei separate, exakt überlappende Blöcke → einer
    // (Daniela) verschwand hinter dem „Teilnehmer hinzufügen"-Platzhalter des
    // anderen. Lösung: Alle Teilnehmer desselben Slots (Service + Startzeit +
    // Trainer) in EINEN Block gruppieren, unabhängig von flex_slot_id.
    //
    // 09/2026 (Ruth-Saxer-Fall LIVE) bleibt erhalten: start_time steckt im
    // Slot-Schlüssel → ein zeitlich verschobener Teilnehmer (der weiterhin die
    // alte flex_slot_id trägt) fällt automatisch in eine eigene Kachel.
    const hasSlotInfo =
      appointment.trainer_id &&
      appointment.start_time &&
      (appointment.service_id || appointment.service_name);
    let key;
    if (appointment._isReservation) {
      key = `reservation:${appointment.id}`;
    } else if (
      hasSlotInfo &&
      ((slotKeyCount.get(slotKeyOf(appointment)) || 0) >= 2 || appointment.flex_slot_id)
    ) {
      key = `slot:${slotKeyOf(appointment)}`;
    } else if (appointment.flex_slot_id) {
      // Fallback: FLEX-Termin ohne vollständige Slot-Infos
      key = `flex:${appointment.flex_slot_id}:${instantOf(appointment.start_time)}:${appointment.trainer_id || ''}`;
    } else {
      key = `appointment:${appointment.id}`;
    }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(appointment);
  });

  return Array.from(groups.values()).map((members) => {
    const sorted = [...members].sort((a, b) => {
      const positionDiff = (a.flex_position ?? 999) - (b.flex_position ?? 999);
      return positionDiff || String(a.id).localeCompare(String(b.id));
    });
    const base = sorted[0];
    if (base._isReservation || sorted.length === 1) {
      return {
        ...base,
        customer_ids: base.customer_id ? [base.customer_id] : [],
        customer_names: base.customer_name ? [base.customer_name] : [],
        _participant_appointments: base.customer_id
          ? { [base.customer_id]: base }
          : {},
      };
    }

    const participantAppointments = {};
    sorted.forEach((member) => {
      if (member.customer_id) participantAppointments[member.customer_id] = member;
    });

    return {
      ...base,
      customer_ids: sorted.map((member) => member.customer_id).filter(Boolean),
      customer_names: sorted.map((member) => member.customer_name || 'Unbekannter Teilnehmer'),
      customer_phones: sorted.map((member) => member.customer_phones?.[0] || ''),
      customer_suit_sizes: sorted.map((member) => member.customer_suit_sizes?.[0] ?? null),
      customer_failed_payments: sorted.map((member) => !!member.customer_failed_payments?.[0]),
      customer_consents_missing: sorted.map((member) => !!member.customer_consents_missing?.[0]),
      _participant_appointments: participantAppointments,
      _flex_member_ids: sorted.map((member) => member.id),
    };
  });
};

// ────────────────────────────────────────────────────────────────────────
//  Calendar Settings
// ────────────────────────────────────────────────────────────────────────

/**
 * Lädt Kalender-Zeiten (start_hour / end_hour) + Studio-Öffnungszeiten.
 * @returns {{calendar: object|null, openingHours: object}}
 */
export const fetchCalendarSettingsApi = async () => {
  let calendar = null;
  let openingHours = {};
  try {
    const res = await axios.get(`${API}/settings/calendar`);
    calendar = res.data || null;
  } catch (e) {
    console.error('Error fetching calendar settings:', e);
  }
  try {
    const res = await axios.get(`${API}/settings/availability`);
    openingHours = res.data || {};
  } catch (e) {
    console.error('Error fetching studio opening hours:', e);
  }
  return { calendar, openingHours };
};

// ────────────────────────────────────────────────────────────────────────
//  Trainers / Services / Customers
// ────────────────────────────────────────────────────────────────────────

export const fetchTrainersApi = async (currentDate, showAllTrainers) => {
  const dateStr = currentDate.toISOString().split('T')[0];
  const res = await axios.get(`${API}/trainers/availability/all`, {
    params: { date: dateStr, show_all: showAllTrainers },
  });
  return res.data || [];
};

export const fetchServicesApi = async () => {
  const res = await axios.get(`${API}/services`);
  return res.data || [];
};

// ────────────────────────────────────────────────────────────────────────
//  Trainer Column Order (per studio) – Iter 318 / Feb 2026
// ────────────────────────────────────────────────────────────────────────

/** Lädt die gespeicherte Trainer-Spalten-Reihenfolge für ein Studio. */
export const fetchTrainerOrderApi = async (studioId) => {
  if (!studioId || studioId === 'all') return [];
  const res = await axios.get(`${API}/trainers/order`, {
    params: { studio_id: studioId },
    headers: authHeaders(),
  });
  return (res.data && res.data.order) || [];
};

/** Speichert die Trainer-Spalten-Reihenfolge für ein Studio. */
export const updateTrainerOrderApi = async (studioId, order) => {
  const res = await axios.put(
    `${API}/trainers/order`,
    { studio_id: studioId, order },
    { headers: authHeaders() },
  );
  return res.data;
};

export const fetchCustomersApi = async () => {
  const res = await axios.get(`${API}/customers/with-users`);
  return res.data || [];
};

export const fetchAnamneseStatusApi = async (customerIds) => {
  const res = await axios.post(`${API}/anamnese/check-status`, customerIds, {
    headers: authHeaders(),
  });
  return res.data || {};
};

// ────────────────────────────────────────────────────────────────────────
//  Appointments + Mindbody + Reservations
// ────────────────────────────────────────────────────────────────────────

/**
 * Holt Termine + Mindbody-Slots + aktive Chatbot-Reservierungen für einen
 * Zeitraum in einem Schwung. Sortiert Reservierungen schon ins
 * Appointment-Format um (`_isReservation: true`).
 *
 * @returns {{appointments: array, mbSlots: array}}
 */
export const fetchAppointmentsApi = async (startDate, endDate, includeCancelled = false) => {
  const [aptRes, mbRes, resRes] = await Promise.allSettled([
    axios.get(`${API}/appointments/with-details`, {
      params: { start_date: startDate, end_date: endDate, include_cancelled: includeCancelled },
    }),
    axios.get(`${API}/admin/mindbody/slots`, {
      params: { start: startDate, end: endDate, limit: 2000, only_mapped: true },
    }),
    axios.get(`${API}/admin/reservations`),
  ]);

  const aptData = aptRes.status === 'fulfilled' ? aptRes.value.data || [] : [];
  // Schalter "Abgesagte anzeigen": bei includeCancelled bleiben abgesagte /
  // no-show Termine erhalten (grau dargestellt), sonst werden sie gefiltert.
  const activeAppointments = includeCancelled
    ? aptData
    : aptData.filter(
        (apt) => apt.status !== 'cancelled' && apt.status !== 'no_show'
      );

  const reservations = resRes.status === 'fulfilled' ? resRes.value.data || [] : [];
  const reservationEntries = reservations.map((r) => ({
    id: `reservation-${r.id}`,
    _isReservation: true,
    _reservationId: r.id,
    _confirmToken: r.confirm_token,
    _expiresAt: r.expires_at,
    customer_id: r.customer_id,
    customer_name: r.customer_name || 'Reservation',
    customer_phone: r.customer_phone || '',
    customer_phones: [r.customer_phone || ''],
    service_id: r.service_id,
    service_name: 'Reserviert (Chatbot)',
    trainer_id: r.trainer_id,
    studio_id: r.studio_id,
    start_time: r.start,
    end_time: r.end,
    status: 'reserved',
  }));

  const mbSlots = mbRes.status === 'fulfilled' ? mbRes.value.data?.slots || [] : [];

  return {
    appointments: [
      ...groupFlexAppointmentsForCalendar(activeAppointments),
      ...reservationEntries,
    ],
    mbSlots,
  };
};

/** Holt nur Mindbody-Slots (für den 15-Sekunden-Auto-Refresh). */
export const fetchMindbodySlotsApi = async (startDate, endDate) => {
  const res = await axios.get(`${API}/admin/mindbody/slots`, {
    params: { start: startDate, end: endDate, limit: 2000, only_mapped: true },
  });
  return res.data?.slots || [];
};

// ────────────────────────────────────────────────────────────────────────
//  Trainer Calendar Decorations (Vacations / Blocks / Exceptions)
// ────────────────────────────────────────────────────────────────────────

/**
 * Sammelt Ferien, Pausen und Verfügbarkeits-Ausnahmen für eine Trainer-Liste
 * an einem Datum.
 * @returns {{vacations: array, blocks: array, exceptions: array}}
 */
export const fetchVacationsAndBlocksApi = async (trainersList, currentDate) => {
  if (!trainersList || trainersList.length === 0) {
    return { vacations: [], blocks: [], exceptions: [] };
  }
  const headers = authHeaders();
  const dateStr = toLocalYMD(currentDate);

  const vacationPromises = [];
  const blockPromises = [];
  const exceptionPromises = [];

  for (const trainerData of trainersList) {
    const trainerId = trainerData.trainer?.id || trainerData.id;
    vacationPromises.push(
      axios.get(`${API}/trainers/${trainerId}/vacations`, { headers }).catch(() => ({ data: [] }))
    );
    blockPromises.push(
      axios.get(`${API}/trainers/${trainerId}/blocks?date=${dateStr}`, { headers }).catch(() => ({ data: [] }))
    );
    exceptionPromises.push(
      axios.get(`${API}/trainers/${trainerId}/exceptions?date=${dateStr}`, { headers }).catch(() => ({ data: [] }))
    );
  }

  const [vacationResults, blockResults, exceptionResults] = await Promise.all([
    Promise.all(vacationPromises),
    Promise.all(blockPromises),
    Promise.all(exceptionPromises),
  ]);

  return {
    vacations: vacationResults.flatMap((r) => r.data || []),
    blocks: blockResults.flatMap((r) => r.data || []),
    exceptions: exceptionResults.flatMap((r) => r.data || []),
  };
};

// ────────────────────────────────────────────────────────────────────────
//  Misc
// ────────────────────────────────────────────────────────────────────────

export const fetchBirthdaysTodayApi = async () => {
  const res = await axios.get(`${API}/dashboard/birthdays-today`);
  return res.data?.birthdays || [];
};

export const fetchPaymentColorsApi = async () => {
  try {
    const res = await axios.get(`${API}/settings/payment-colors`);
    if (res.data && res.data.pending) return res.data;
  } catch (e) {
    console.error('Error fetching payment colors:', e);
  }
  return null; // Caller behält Default-Farben
};

export const fetchAvailableServicesApi = async (trainerId, date, time) => {
  const res = await axios.get(
    `${API}/trainers/${trainerId}/available-services?date=${date}&time=${time}`,
    { headers: authHeaders() }
  );
  return res.data || { all_services_allowed: true, services: [], reason: '' };
};

/** Stösst einen manuellen Mindbody-Sync an. Liefert `stats`. */
export const triggerMindbodySyncApi = async () => {
  const res = await axios.post(`${API}/admin/mindbody/sync-now`);
  return res.data || {};
};

/** Stösst die manuelle „Alle offenen Termine abrechnen"-Aktion an. */
export const runManualBillingApi = async () => {
  const res = await axios.post(
    `${API}/billing/process-all-pending`,
    {},
    { headers: authHeaders() }
  );
  return res.data || {};
};

/** Pollt den Status eines Hintergrund-Abrechnungslaufs. */
export const fetchBillingRunStatusApi = async (runId) => {
  const res = await axios.get(`${API}/billing/runs/${runId}`, {
    headers: authHeaders(),
  });
  return res.data || {};
};

/** Lädt die Studio-Liste für den Header-Switcher. */
export const fetchStudiosApi = async () => {
  const res = await axios.get(`${API}/studios`, { headers: authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
};
